<template>
    <form
        class="flex flex-col gap-y-4"
        @submit="handleSubmitCheckoutForm"
    >
        <billing-address
            v-model:editing="editingBillingAddress"
            :customer="null"
            :is-full-address="fullAddressRequired"
            title="Billing Address"
        />

        <div class="-mx-4 mt-2 h-4 bg-gray-50 sm:hidden" />

        <recurring-information
            v-if="requiresRecurringToken"
            ref="recurringInformationRef"
            :customer="primaryCustomer"
            :facility="facility"
            :recurring-total="recurringTotal"
        />

        <div
            v-if="readyForPayment"
            class="-mx-4 mt-2 h-4 bg-gray-50 sm:hidden"
        />

        <div
            v-else
            class="mt-4"
        >
            <prime-button
                label="Continue to Payment"
                type="submit"
                class="w-full"
                :loading="isValidatingRecurring"
            />
        </div>

        <payment-information
            v-if="readyForPayment"
            ref="paymentInformationRef"
            :customer="primaryCustomer"
            :facility="facility"
            :checkout-total="total"
            :title="paymentTitle"
            :allow-use-existing="allowUseExisting"
        />

        <document-section
            v-if="readyForPayment"
            :contract="contract"
            :is-signing-document="
                isCommittingInlineCheckout || isValidatingPayment
            "
            :balance-due-mode="false"
            :invalid-initials="invalidInitials"
            :invalid-signatures="invalidSignatures"
            @inline-checkout-document="handleInlineCheckoutDocument"
            @inline-checkout="handleInlineCheckout"
        />

        <div
            v-if="showPleaseFixMessage"
            class="text-red-600 mt-4 font-medium"
        >
            Please fix the errors in red above.
        </div>
    </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { object } from 'yup'
import { useForm } from 'vee-validate'
import { Decimal } from '~/composables/support/decimal'
import { CheckoutSuccessFragment } from '~/components/Checkout/CheckoutSuccess.vue'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { useMutation } from '@tanstack/vue-query'
import type {
    CommitCheckoutInput,
    DocumentInput
} from '~/resources/graphql/graphql'
import { computed, ref, toRef } from 'vue'
import { type FragmentType, graphql } from '~/resources/graphql'
import PrimeButton from 'primevue/button'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'
import { GraphQLRequestError } from 'awesome-graphql-client'

import {
    CheckoutFormCheckoutFragment,
    CheckoutFormQueryFragment,
    useCheckoutForm
} from '~/components/Checkout/Forms/use-checkout-form'

import BillingAddress, {
    BillingAddressInitialValues,
    BillingAddressSchema
} from '~/components/Checkout/Sections/BillingAddress.vue'
import RecurringInformation, {
    RecurringInformationInitialValues,
    RecurringInformationSchema
} from '~/components/Checkout/Sections/RecurringInformation.vue'
import PaymentInformation, {
    type GiftCardPaymentInfo,
    PaymentInformationInitialValues,
    PaymentInformationSchema
} from '~/components/Checkout/Sections/PaymentInformation.vue'
import DocumentSection, {
    DocumentSectionContractFragment
} from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import { useShowAlert } from '#imports'

const props = defineProps<{
    query: FragmentType<typeof CheckoutFormQueryFragment>
    checkout: FragmentType<typeof CheckoutFormCheckoutFragment>
    contract?: FragmentType<typeof DocumentSectionContractFragment>
}>()

const emit = defineEmits<{
    (
        e: 'checkoutSuccess',
        payload: FragmentType<typeof CheckoutSuccessFragment>
    ): void
}>()

const {
    checkoutData,

    primaryCustomer,
    facility,

    fullAddressRequired,

    editingBillingAddress,

    total,
    recurringTotal,
    requiresRecurringToken
} = useCheckoutForm(toRef(props, 'query'), toRef(props, 'checkout'))
editingBillingAddress.value = true

const schema = toTypedSchema(
    object({
        address: BillingAddressSchema,
        recurring: RecurringInformationSchema,
        payment: PaymentInformationSchema
    })
)

const {
    handleSubmit,
    errors,
    controlledValues,
    validateField,
    validate,
    setFieldError
} = useForm({
    validationSchema: schema,
    initialValues: {
        address: BillingAddressInitialValues(null, fullAddressRequired.value),
        recurring: RecurringInformationInitialValues(
            checkoutData.value.recurringTotal
        ),
        payment: PaymentInformationInitialValues(checkoutData.value.total)
    }
})

const showPleaseFixMessage = computed(() => {
    // No submission yet means don't show the error message
    if (submitCount.value < 1) {
        return false
    }

    // If we are on the pay now portion, show all errors
    if (readyForPayment.value) {
        return Object.keys(errors.value).length > 0
    }

    // Otherwise show errors with payment filtered out
    return (
        Object.keys(errors.value).filter(field => !field.startsWith('payment'))
            .length > 0
    )
})

const pendingRecurringToken = ref(requiresRecurringToken.value)
const readyForPayment = computed(() => pendingRecurringToken.value === false)
const recurringInformationRef = ref<InstanceType<typeof RecurringInformation>>()
const paymentInformationRef = ref<InstanceType<typeof PaymentInformation>>()
const isValidatingRecurring = ref(false)
const isValidatingPayment = ref(false)
// We use our own submit count because the continue to payment button does not submit the vee validate form
const submitCount = ref(0)
const paymentTitle = computed(() => {
    if (requiresRecurringToken.value) {
        return 'Payment Due Today'
    } else {
        return 'Payment'
    }
})

// If we are not using a bank account, let them use the same card
const allowUseExisting = computed(() => {
    return (
        controlledValues.value.recurring !== undefined &&
        controlledValues.value.recurring.bankAccount === null
    )
})

async function handleSubmitCheckoutForm(event?: SubmitEvent) {
    // Prevent form action
    event?.preventDefault()
    submitCount.value += 1

    if (readyForPayment.value) {
        isValidatingPayment.value = true
        // If payment field is not valid, submit stripe form
        // Use mode: silent so we don't flash, the error message
        const { valid: validPayment } = await validateField('payment', {
            mode: 'silent'
        })
        if (!validPayment) {
            await paymentInformationRef.value.submitStripeForm()
        }

        isValidatingPayment.value = false

        return onSubmit(event)
    }

    // Recurring checkout
    try {
        isValidatingRecurring.value = true

        // If recurring field is not valid, submit stripe form
        // We use silent so we don't show the error message until after stripe form is submitted
        const { valid: validRecurring } = await validateField('recurring', {
            mode: 'silent'
        })
        if (!validRecurring) {
            await recurringInformationRef.value.submitRecurringForm()
        }

        // Otherwise, validate the entire form
        const result = await validate()
        const isValid = Object.entries(result.results).every(([key, value]) => {
            return key === 'payment' || value.valid
        })

        // Also clear this error so we don't flash red on the payment error
        setFieldError('payment', undefined)

        if (isValid) {
            // Close all the other pieces and we are ready for payment
            submitCount.value = 0 // reset submit count
            editingBillingAddress.value = false
            pendingRecurringToken.value = false
        } else {
            // TODO: Find a way to scroll up
        }
    } finally {
        isValidatingRecurring.value = false
    }
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const query = useRpQuery()
const { mutate: commitInlineCheckout, isPending: isCommittingInlineCheckout } =
    useMutation({
        mutationFn: async (mutationData: CommitCheckoutInput) => {
            const response = await query(
                graphql(/** GraphQL */ `
                    mutation CommitInlineCheckout(
                        $input: CommitInlineCheckoutInput!
                    ) {
                        commitInlineCheckout(input: $input) {
                            __typename
                            ... on CommitCheckoutResult {
                                ...CheckoutSuccessFragment
                            }
                            ... on ValidationError {
                                fieldErrors {
                                    path
                                    message
                                }
                            }
                        }
                    }
                `),
                {
                    input: mutationData
                }
            )

            if (response.commitInlineCheckout === null) {
                throw new Error(
                    'commitInlineCheckout guest null return. Please try again.'
                )
            }

            if (
                response.commitInlineCheckout.__typename ===
                    'CommitCheckoutResult' ||
                response.commitInlineCheckout.__typename === 'ValidationError'
            ) {
                return response.commitInlineCheckout
            }

            throw new Error()
        }
    })

const showAlert = useShowAlert()
const savedDocumentInput = ref()
const onSubmit = handleSubmit(values => {
    // Customer and Address
    const customer = {
        address: {
            ...values.address,
            // Wipe out the full address
            fullAddress: undefined
        }
    }

    // Recurring Token
    const recurring = requiresRecurringToken.value
        ? {
              ...values.recurring
          }
        : null

    // Payment Items
    const payment = {
        items: [] as {
            paymentSourceId: string
            amount: string
            token: string
        }[]
    }

    // Add any gift cards
    const giftCards = values.payment.giftCards
    if (giftCards !== undefined && giftCards.length > 0) {
        payment.items.push(
            ...giftCards.map(giftCard => {
                return {
                    paymentSourceId: giftCard.paymentSourceId,
                    amount: giftCard.amount,
                    token: giftCard.token
                }
            })
        )
    }

    // Use recurring payment method
    if (values.payment.useStoredPaymentMethod) {
        let cardAmount = new Decimal(values.payment.total)
        if (giftCards !== undefined && giftCards.length > 0) {
            giftCards.forEach((giftCard: GiftCardPaymentInfo) => {
                cardAmount = cardAmount.minus(new Decimal(giftCard.amount))
            })
        }

        payment.items.push({
            paymentSourceId: values.recurring.creditCard.paymentSourceId,
            amount: cardAmount.toFixed(2),
            token: 'USE_STORED_PAYMENT_METHOD'
        })
    }
    // Otherwise, may have credit card if gift card does not cover total
    else {
        const creditCard = values.payment.creditCard
        if (creditCard !== null && creditCard.token !== undefined) {
            payment.items.push(creditCard)
        }
    }

    commitInlineCheckout(
        {
            checkoutId: checkoutData.value.id,
            customer,
            recurring,
            payment,
            document: savedDocumentInput.value
        },
        {
            onSuccess: data => {
                if (data.__typename === 'ValidationError') {
                    handleValidationErrors(
                        data.fieldErrors,
                        invalidInitials,
                        invalidSignatures
                    )

                    let messageBuilder = ''
                    data.fieldErrors.forEach(error => {
                        if (
                            error.path !== 'initials' &&
                            error.path !== 'signatures'
                        ) {
                            messageBuilder += error.message
                        }
                    })
                    if (messageBuilder !== '') {
                        showAlert('Heads Up', messageBuilder)
                    }
                } else {
                    emit('checkoutSuccess', data)
                }
            },
            onError(error, variables, context) {
                if (
                    !(error instanceof UserReportableError) &&
                    !(error instanceof GraphQLRequestError)
                ) {
                    showAlert('Heads Up', String(error))
                }
            }
        }
    )
})

function handleInlineCheckoutDocument(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
}

function handleInlineCheckout(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
    handleSubmitCheckoutForm().catch(error => {
        showAlert('Heads Up', String(error))
    })
}
</script>
